import { CATALOG_REGISTER_BATCH_SIZE, type CloudCatalogEntry, type Comic } from '@tanko/shared'
import { assignCatalogIds, markCatalogRegistered } from '../db/repo'
import { db } from '../db/schema'
import type { CloudApiClient } from './api-client'
import { errorText, type BulkOutcome } from './bulk-progress'

/**
 * 目錄註冊。
 *
 * 註冊只讀 IndexedDB 既有的欄位——標題、作者、頁數、封面頁在作品加入並完成索引時
 * 就已寫入，因此不呼叫 File System Access、不開啟壓縮檔、不讀取任何圖片。
 * 這讓升級後的一次性遷移能在尚未授權任何來源資料夾的情況下完成。
 */

/** 一次至多 100 本：失敗重試的成本有界，往返次數又不隨作品數線性成長。 */
export function registrationBatches(comics: readonly Comic[]): Comic[][] {
  const batches: Comic[][] = []
  for (let index = 0; index < comics.length; index += CATALOG_REGISTER_BATCH_SIZE) {
    batches.push(comics.slice(index, index + CATALOG_REGISTER_BATCH_SIZE))
  }
  return batches
}

/**
 * 取出註冊所需的欄位。
 *
 * 不帶 libraryId 與 relPath：雲端不需要硬碟上的目錄結構。封面頁落在頁數之外時
 * 退回第一頁——送出去只會讓整批因為 comic_cover 而被拒絕，而封面是可以重設的。
 */
export function toCatalogEntry(comic: Comic): CloudCatalogEntry {
  if (!comic.cloudComicId) throw new Error(`作品「${comic.title}」還沒有雲端目錄識別碼，無法註冊。`)
  const withinRange = Number.isInteger(comic.coverPage) && comic.coverPage >= 0 && comic.coverPage < comic.pageCount
  return {
    id: comic.cloudComicId,
    title: comic.title.trim(),
    author: comic.author.trim(),
    pageCount: comic.pageCount,
    coverPage: withinRange ? comic.coverPage : 0,
    // 雲端目錄項的建立時間用這一個，雲端的時間排序才與本地一致。
    addedAt: comic.addedAt,
  }
}

type RegisterClient = Pick<CloudApiClient, 'registerCatalog'>

/** 尚未在雲端目錄註冊的作品。索引未建立完成的不納入——頁數還不確定。 */
export async function unregisteredComics(): Promise<Comic[]> {
  return db.comics.where('syncState').equals('unregistered').filter(comic => comic.indexed).toArray()
}

/**
 * 已經在雲端目錄裡的作品。
 *
 * 供一次性修復使用：早期的註冊把整批的建立時間寫成註冊當下那個「現在」，於是雲端所有
 * 作品共用同一個時間戳，時間排序全部平手而失效。重送同一批會把建立時間對回本地的加入
 * 時間；註冊本身是冪等的，重送不會建立第二筆，也不會覆寫標題、作者或備份狀態。
 */
export async function registeredComics(): Promise<Comic[]> {
  return db.comics
    .filter(comic => comic.indexed && !!comic.cloudComicId && comic.syncState !== 'unregistered')
    .toArray()
}

/**
 * 把尚未註冊的作品分批送進雲端目錄，遇錯即停。
 *
 * 先為整批配發並持久化識別碼，再送出：中斷後重試會帶同一組識別碼，因此不會重複建立。
 * 每一批成功才標記該批，未送出的維持未註冊狀態留待下一輪。
 */
export async function registerComics(
  comics: readonly Comic[], api: RegisterClient, onProgress?: (completed: number) => void,
): Promise<BulkOutcome> {
  const completedIds: string[] = []
  if (!comics.length) return { completed: 0, completedIds, reason: '' }
  try {
    await assignCatalogIds(comics.map(comic => comic.id))
  } catch (cause) {
    return { completed: 0, completedIds, reason: errorText(cause), cause }
  }
  // 重讀以取得剛配發的識別碼，並排除配發期間已被移除的作品。
  const assigned = (await db.comics.bulkGet(comics.map(comic => comic.id)))
    .filter((comic): comic is Comic => !!comic?.cloudComicId)
  for (const batch of registrationBatches(assigned)) {
    try {
      await api.registerCatalog(batch.map(toCatalogEntry))
      await markCatalogRegistered(batch.map(comic => comic.id))
    } catch (cause) {
      return { completed: completedIds.length, completedIds, reason: errorText(cause), cause }
    }
    completedIds.push(...batch.map(comic => comic.id))
    onProgress?.(completedIds.length)
  }
  return { completed: completedIds.length, completedIds, reason: '' }
}
